import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { EuiFieldNumber, EuiInMemoryTable, EuiSearchBar, EuiSpacer, EuiSwitch, SortDirection } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'
import { EuiSearchBarOnChangeArgs } from '@elastic/eui/src/components/search_bar/search_bar'

import { Account } from '../../api/interfaces'
import { ReserveSettings } from '../../api/interfaces/ReserveSettings'
import { useGetAccountsQuery, useSetAccountAgencyReserveSettingsMutation } from '../../api/rtkQueryApi/platform/accountsApi'
import { RootState } from '../../app/rootReducer'
import { DEFAULT_INITIAL_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '../../utils/DefaultPageSize'
import useDebounce from '../../utils/useDebounce'

export const ClientsAgencySettings: React.FC = () => {
  const [queryText, setQueryText] = useState('')
  const { currentOrganization } = useSelector((state: RootState) => state.app)
  const accountsQuery = useGetAccountsQuery({ isAgency: true })
  const [displayedClients, setDisplayedClients] = useState<Account[]>([])
  const [setAccountAgencyReserveSettings] = useSetAccountAgencyReserveSettingsMutation()
  const [editedClients, setEditedClients] = useState<Account[]>([])
  const debouncedEditedClients = useDebounce(editedClients, 2000)
  const [isUpdatingClients, setIsUpdatingClients] = useState(false)
  const [currentPage, setCurrentPage] = useState<number>(0)

  useEffect(() => {
    if (accountsQuery.data && !isUpdatingClients && !accountsQuery.isFetching) {
      const filteredAccounts = EuiSearchBar.Query.execute(queryText, accountsQuery.data, {
        defaultFields: ['name', 'email', 'id']
      })
      setDisplayedClients(filteredAccounts)
    }
  }, [accountsQuery, queryText, isUpdatingClients])

  useEffect(() => {
    if (debouncedEditedClients.length === 0) {
      return
    }
    setIsUpdatingClients(true)
    const tasks = debouncedEditedClients.map((client: Account) => {
      return setAccountAgencyReserveSettings({
        accountId: client.id,
        agencyReserveSettings: client.accountReserveSettings ?? { inheritAgencyReserveRate: true, reserveRate: 0 }
      }).unwrap()
    })
    Promise.all(tasks).finally(() => {
      setEditedClients([])
      setIsUpdatingClients(false)
    })
  }, [debouncedEditedClients])

  const searchOnChange = ({ queryText, error }: EuiSearchBarOnChangeArgs) => {
    if (error) {
      return
    } else {
      setQueryText(queryText)
    }
  }

  const onAgencyReserveSettingsChange = (accountId: string, settings: ReserveSettings) => {
    const editedClient = displayedClients.find(acc => acc.id === accountId)
    setDisplayedClients(prevClients => prevClients.map(client => (client.id === accountId ? { ...client, accountReserveSettings: settings } : client)))
    setEditedClients(prevClients => {
      let excludePrevClients = prevClients.filter(c => c.id !== accountId)
      excludePrevClients.push({ ...editedClient, accountReserveSettings: settings } as Account)
      return excludePrevClients
    })
  }

  const clientAgencyReserveSettingsColumns: Array<EuiBasicTableColumn<Account>> = [
    {
      name: 'Client Name',
      field: 'name',
      sortable: true
    },
    {
      name: 'Use Custom Reserve Rate',
      render: (account: Account) => (
        <EuiSwitch
          id={`inheritAgencyReserveSwitch-${account.id}`}
          checked={!(account.accountReserveSettings?.inheritAgencyReserveRate ?? true)}
          onChange={() => {
            const newSettings = {
              inheritAgencyReserveRate: !(account.accountReserveSettings?.inheritAgencyReserveRate ?? true),
              reserveRate: account.accountReserveSettings?.reserveRate ?? 0
            }
            onAgencyReserveSettingsChange(account.id, newSettings)
          }}
          label={''}
        />
      ),
      sortable: (account: Account) => account.accountReserveSettings?.inheritAgencyReserveRate ?? true
    },
    {
      name: 'Reserve Rate',
      render: (account: Account) =>
        account.accountReserveSettings?.inheritAgencyReserveRate ?? true ? (
          `${((currentOrganization?.organizationReserveSettings?.reserveRate ?? 0) * 100).toFixed()}%`
        ) : (
          <EuiFieldNumber
            type='number'
            value={((account.accountReserveSettings?.reserveRate ?? 0) * 100).toFixed()}
            onChange={e => {
              const newRate = Math.min(Math.max(Number(e.target.value) / 100, 0), 0.2)
              const newSettings = {
                inheritAgencyReserveRate: false,
                reserveRate: newRate
              }
              onAgencyReserveSettingsChange(account.id, newSettings)
            }}
            append={'%'}
          />
        ),
      sortable: (account: Account) => account.accountReserveSettings?.reserveRate ?? 0
    }
  ]

  const sorting = {
    sort: {
      field: 'name',
      direction: SortDirection.ASC
    }
  }

  const pagination = {
    initialPageSize: DEFAULT_INITIAL_PAGE_SIZE,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    pageIndex: currentPage
  }

  return (
    <>
      <EuiSearchBar box={{ incremental: true }} onChange={searchOnChange} />
      <EuiSpacer />
      <EuiInMemoryTable items={displayedClients} responsive={false} columns={clientAgencyReserveSettingsColumns} sorting={sorting} pagination={pagination} onTableChange={({ page: { index } }) => setCurrentPage(index)} />
    </>
  )
}
